/**
 * What a reminder plan is CALLED on the Scheduled sends list, and which closed
 * plan a rescheduled one replaced.
 *
 * Every block used to read "Reminder plan". With two plans on one trip, or a
 * reschedule (which closes the old plan and opens a new one), the list was a
 * stack of identical headers with nothing to tell them apart and no hint that
 * one was the other's replacement.
 */
import { momentParts } from './changeNotice';
import { plural } from './plural';
import { IN_DOUBT_CANCELLED_BY, planAnchorDate, tripDateLabel, type ScheduledListItem } from './reminderPlan';
import { rescheduledSequenceId } from './uuidV5';
import type { GroupScheduledBroadcast } from '../types/groups';

type PlanItem = Extract<ScheduledListItem, { kind: 'plan' }>;

const byTime = (a: GroupScheduledBroadcast, b: GroupScheduledBroadcast) => Date.parse(a.sendAt) - Date.parse(b.sendAt);

export interface PlanName {
  /** "Reminders for Fri Oct 16 · 4 steps" */
  title: string;
  /** The earliest step's subject, which tells two plans for the same date apart. Null when it has none. */
  firstStep: string | null;
}

/**
 * Named from the trip date the plan was built for (the anchor the API stored on its rows) and its step
 * count. A plan with no anchor (made before it existed) is named from where it starts instead, rather than
 * guessing a trip date from the copy. The earliest step's subject rides along as `firstStep`.
 */
export function planName(rows: readonly GroupScheduledBroadcast[], timeZone: string): PlanName {
  const earliest = [...rows].sort(byTime)[0];
  const anchor = planAnchorDate(rows);
  const steps = plural(rows.length, 'step');
  let head: string;
  if (anchor) head = `Reminders for ${tripDateLabel(anchor)}`;
  else if (earliest?.sendAt && !Number.isNaN(Date.parse(earliest.sendAt))) head = `Reminders from ${momentParts(earliest.sendAt, timeZone).date.replace(',', '')}`;
  else head = 'Reminders';
  return { title: `${head} · ${steps}`, firstStep: earliest?.subject?.trim() || null };
}

/** The statuses a reminder never leaves. Anything else (including a status this build has never seen) may still go out. */
const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['sent', 'skipped', 'failed', 'cancelled']);

/** A plan with no live reminder left: every row is sent, skipped, failed or cancelled (a whitelist, so an unknown status keeps the plan open). */
export const isClosedPlan = (rows: readonly GroupScheduledBroadcast[]): boolean => (
  rows.length > 0 && rows.every((row) => TERMINAL_STATUSES.has(row.status))
);

export type FoldedPlanItem = ScheduledListItem & { replaced?: PlanItem[] };

/**
 * Does `newer` descend from `older`? Only when the data says so: a reschedule gives the new plan the id
 * `rescheduledSequenceId(oldId, newAnchorDate)`, so `newer` replaces `older` exactly when that id
 * is `newer`'s own. Nothing is guessed from names, dates or timing. A reschedule may also have moved
 * the date by a whole number of days that the new plan's anchor already names, so the anchor is the only input.
 */
export function replacesPlan(newer: PlanItem, older: PlanItem): boolean {
  if (newer.sequenceId === older.sequenceId) return false;
  const anchor = planAnchorDate(newer.rows);
  return Boolean(anchor) && rescheduledSequenceId(older.sequenceId, anchor as string) === newer.sequenceId;
}

/**
 * Folds each CLOSED plan under the plan that replaced it, instead of leaving it as a peer block.
 * A plan that is linked but still has a live reminder stays on the list by itself: hiding something
 * that can still send would be worse than the clutter. Everything else passes through in order.
 */
export function foldReplacedPlans(items: readonly ScheduledListItem[]): FoldedPlanItem[] {
  const plans = items.filter((item): item is PlanItem => item.kind === 'plan');
  // The closed plans `plan` replaced, and the ones THEY replaced (a plan rescheduled twice has two ancestors).
  const ancestors = (plan: PlanItem, seen: Set<string> = new Set([plan.sequenceId])): PlanItem[] => {
    const found: PlanItem[] = [];
    for (const older of plans) {
      if (seen.has(older.sequenceId) || !isClosedPlan(older.rows) || !replacesPlan(plan, older)) continue;
      seen.add(older.sequenceId);
      found.push(older, ...ancestors(older, seen));
    }
    return found;
  };
  const hidden = new Set<string>();
  const folded: FoldedPlanItem[] = items.map((item) => {
    if (item.kind !== 'plan') return item;
    const replaced = ancestors(item);
    replaced.forEach((older) => hidden.add(older.sequenceId));
    return replaced.length > 0 ? { ...item, replaced } : item;
  });
  // Only the head of a chain stays a peer; the rest are reachable beneath it.
  return folded.filter((item) => item.kind !== 'plan' || !hidden.has(item.sequenceId));
}

/**
 * What a host must not lose sight of when an old plan is folded under its replacement: a reminder that FAILED, and one that
 * MAY have been sent (cancelled by a reschedule as "in doubt"). Counted over every plan folded in; null when there is none,
 * so the fold's head says nothing it does not need to.
 */
export function replacedPlanFlags(replaced: ReadonlyArray<{ rows: readonly GroupScheduledBroadcast[] }>): string | null {
  const rows = replaced.flatMap((older) => older.rows);
  const failed = rows.filter((row) => row.status === 'failed').length;
  const inDoubt = rows.filter((row) => (row.cancelledBy ?? '').startsWith(IN_DOUBT_CANCELLED_BY)).length;
  const parts = [
    failed > 0 ? `${failed} failed` : null,
    inDoubt > 0 ? `${inDoubt} may have been sent` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}
